import api from './client';
import { notifyItemSeen } from './newCounts';
import { uploadSrc } from './uploadUrl';

export type OrderKind = 'legal' | 'maintenance';
export type OrderStatus =
  | 'submitted'
  | 'quoted'
  | 'accepted'
  | 'declined'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

export const ORDER_STATUSES: OrderStatus[] = [
  'submitted',
  'quoted',
  'accepted',
  'declined',
  'in_progress',
  'completed',
  'cancelled',
];

/**
 * Statuses an admin may set from the orders table. 'submitted', 'quoted' and
 * 'declined' are produced by the customer/provider flows, not chosen by admin,
 * so they stay readable (filters, badges) but are not offered as an update.
 */
export const ADMIN_SETTABLE_ORDER_STATUSES: OrderStatus[] = [
  'accepted',
  'in_progress',
  'completed',
  'cancelled',
];

export interface OrderCustomer {
  id: number;
  name: string | null;
  email: string;
}

export interface ServiceOrder {
  id: number;
  orderRef: string | null;
  /** Admin list only: no admin has opened it yet. */
  isNew?: boolean;
  userId: number;
  kind: OrderKind;
  serviceId: number | null;
  serviceName: string | null;
  tier: string | null;
  category: string | null;
  description: string | null;
  amount: string | number | null;
  currency: string;
  paid: boolean;
  paidAt: string | null;
  status: OrderStatus;
  providerId: number | null;
  providerName: string | null;
  /*
   * NOT raw history. The backend replaces the stored column with a full
   * stepper (buildTimelineSteps), appending every stage the order has NOT
   * reached yet as { at: null, detail: null, state: 'pending' }. Anything
   * rendering this MUST branch on `state`, or unreached stages will read as
   * things that already happened.
   */
  timeline?: Array<{
    status: string;
    label: string;
    at: string | null;
    detail?: string | null;
    state?: 'completed' | 'current' | 'pending';
  }>;
  createdAt?: string;
  updatedAt?: string;
  customer?: OrderCustomer;

  /* Everything below is what the customer filled in when submitting a
     maintenance request. The list endpoint already returns these (the backend
     serialises the whole row), they simply weren't typed before. */
  photos?: string[];
  preferredDate?: string | null;
  timeSlot?: 'morning' | 'afternoon' | 'evening' | null;
  urgency?: 'standard' | 'urgent' | null;
  quotedAmount?: string | number | null;
  quotedAt?: string | null;
  provider?: OrderCustomer | null;
  service?: { id: number; name: string | null; pricingType?: string | null } | null;
}

/** Resolve a stored photo URL for <img src> (see uploadSrc). */
export const photoSrc = uploadSrc;

export interface OrderListParams {
  kind?: OrderKind;
  status?: OrderStatus;
  userId?: number;
  page?: number;
  limit?: number;
}

export interface OrderListResponse {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  orders: ServiceOrder[];
}

interface RawListResponse extends OrderListResponse {
  status: number;
}

interface MutationResponse {
  status: number;
  message?: string;
  error?: string | string[];
  order?: ServiceOrder;
}

/** GET /api/orders — admin sees all orders; provider sees assigned. */
export const listOrders = async (
  params: OrderListParams = {}
): Promise<OrderListResponse> => {
  const query: Record<string, string> = {};
  if (params.kind) query.kind = params.kind;
  if (params.status) query.status = params.status;
  if (params.userId !== undefined) query.userId = String(params.userId);
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);

  const res = await api.get<RawListResponse>('/orders', { params: query });
  return {
    total: res.data.total,
    page: res.data.page,
    limit: res.data.limit,
    totalPages: res.data.totalPages,
    orders: res.data.orders,
  };
};

/** GET /api/orders/:id */
export const getOrder = async (id: number): Promise<ServiceOrder> => {
  const res = await api.get<{ status: number; order: ServiceOrder }>(`/orders/${id}`);
  notifyItemSeen();
  return res.data.order;
};

/** PATCH /api/orders/:id/status — admin or provider. */
export const updateOrderStatus = async (
  id: number,
  status: OrderStatus,
  detail?: string
): Promise<ServiceOrder> => {
  const res = await api.patch<MutationResponse>(`/orders/${id}/status`, {
    status,
    detail,
  });
  if (!res.data.order) throw new Error('No order returned');
  notifyItemSeen();
  return res.data.order;
};

/** Human-readable label for a status, e.g. 'in_progress' -> 'In Progress'. */
export const statusLabel = (s: OrderStatus): string =>
  s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/** Maps a status to one of the app's badge-* classes. */
export const statusBadgeClass = (s: OrderStatus): string => {
  const map: Record<OrderStatus, string> = {
    submitted: 'badge-new',
    quoted: 'badge-pending',
    accepted: 'badge-converted',
    declined: 'badge-rejected',
    in_progress: 'badge-pending',
    completed: 'badge-active',
    cancelled: 'badge-rejected',
  };
  return `badge ${map[s]}`;
};

/** Formats the backend's stringified DECIMAL amount into "$50.00" / "—". */
export const formatAmount = (amount: ServiceOrder['amount']): string => {
  if (amount === null || amount === undefined || amount === '') return '—';
  const num = typeof amount === 'number' ? amount : Number(amount);
  if (Number.isNaN(num)) return String(amount);
  return num % 1 === 0 ? `$${num}` : `$${num.toFixed(2)}`;
};

/** Pulls a human-readable error message out of an axios error. */
export const extractApiError = (err: unknown, fallback = 'Request failed'): string => {
  const e = err as {
    response?: { data?: { error?: string | string[]; message?: string } };
    message?: string;
  };
  const data = e?.response?.data;
  if (data?.error) {
    return Array.isArray(data.error) ? data.error.join(', ') : data.error;
  }
  if (data?.message) return data.message;
  if (e?.message) return e.message;
  return fallback;
};
